import { type ReactNode } from "react";

export interface TileOption<V extends string = string> {
  value: V;
  label: string;
  hint?: string;
  icon?: ReactNode;
}

type Accent = "teal" | "lav" | "rose" | "mint" | "peach" | "sky";

interface SingleProps<V extends string> {
  value: V | "";
  onChange: (v: V) => void;
  options: TileOption<V>[];
  columns?: 2 | 3 | 4;
  accent?: Accent;
}

const ACCENT_STYLES: Record<Accent, { ring: string; bg: string; text: string; dot: string }> = {
  teal: { ring: "ring-teal-300", bg: "bg-teal-50", text: "text-teal-700", dot: "bg-teal-400" },
  lav: { ring: "ring-lav-200", bg: "bg-lav-50", text: "text-royal", dot: "bg-lav-glow" },
  rose: { ring: "ring-rose-200", bg: "bg-rose-50", text: "text-rose-glow", dot: "bg-rose-glow" },
  mint: { ring: "ring-mint-200", bg: "bg-mint-50", text: "text-mint-glow", dot: "bg-mint-glow" },
  peach:{ ring: "ring-peach-200", bg: "bg-peach-50", text: "text-peach-glow", dot: "bg-peach-glow" },
  sky:  { ring: "ring-sky-200", bg: "bg-sky-50", text: "text-sky-glow", dot: "bg-sky-glow" },
};

export function TilePicker<V extends string>({
  value,
  onChange,
  options,
  columns = 2,
  accent = "lav",
}: SingleProps<V>) {
  const a = ACCENT_STYLES[accent];
  const cols = { 2: "sm:grid-cols-2", 3: "sm:grid-cols-3", 4: "sm:grid-cols-2 md:grid-cols-4" }[columns];
  return (
    <div className={`grid gap-2 ${cols}`}>
      {options.map((o) => {
        const selected = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`group relative text-left rounded-xl border px-4 py-3 transition outline-none
              ${
                selected
                  ? `${a.bg} border-transparent ring-2 ${a.ring} shadow-sm`
                  : "bg-white/70 border-ink-200/40 hover:border-ink-300 hover:bg-white"
              }`}
          >
            <span className="flex items-center gap-3">
              <span
                className={`inline-block h-2.5 w-2.5 rounded-full transition ${
                  selected ? a.dot : "bg-ink-200 group-hover:bg-ink-300"
                }`}
              />
              <span className="flex-1">
                <span className={`block font-semibold text-sm ${selected ? a.text : "text-ink-900"}`}>
                  {o.label}
                </span>
                {o.hint && (
                  <span className="block text-xs text-ink-500 mt-0.5 leading-snug">
                    {o.hint}
                  </span>
                )}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

interface MultiProps {
  values: string[];
  onToggle: (v: string) => void;
  options: TileOption[];
  columns?: 2 | 3 | 4;
  accent?: Accent;
}

export function TileMultiPicker({
  values,
  onToggle,
  options,
  columns = 2,
  accent = "lav",
}: MultiProps) {
  const a = ACCENT_STYLES[accent];
  const cols = { 2: "sm:grid-cols-2", 3: "sm:grid-cols-3", 4: "sm:grid-cols-2 md:grid-cols-4" }[columns];
  return (
    <div className={`grid gap-2 ${cols}`}>
      {options.map((o) => {
        const selected = values.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onToggle(o.value)}
            className={`group relative text-left rounded-xl border px-4 py-3 transition outline-none
              ${
                selected
                  ? `${a.bg} border-transparent ring-2 ${a.ring} shadow-sm`
                  : "bg-white/70 border-ink-200/40 hover:border-ink-300 hover:bg-white"
              }`}
          >
            <span className="flex items-center gap-3">
              <span
                className={`relative inline-flex h-5 w-5 items-center justify-center rounded-md border transition ${
                  selected
                    ? `${a.bg} border-transparent ring-2 ${a.ring}`
                    : "border-ink-300 bg-white"
                }`}
              >
                {selected && (
                  <svg
                    viewBox="0 0 24 24"
                    className={`h-3.5 w-3.5 ${a.text}`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="5 12 10 17 19 7" />
                  </svg>
                )}
              </span>
              <span className="flex-1">
                <span className={`block font-semibold text-sm ${selected ? a.text : "text-ink-900"}`}>
                  {o.label}
                </span>
                {o.hint && (
                  <span className="block text-xs text-ink-500 mt-0.5 leading-snug">
                    {o.hint}
                  </span>
                )}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
